// Cinematic hero helpers shared by PageVideoHero and SequentialVideoHero:
// scroll-driven exit (media zooms + drifts, CTAs lift away), HUD chrome with
// crop marks and a live Accra clock, and magnetic CTA buttons.
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { motion, useReducedMotion, useScroll, useSpring, useTransform, type MotionValue } from "framer-motion";

export function useHeroScroll(ref: RefObject<HTMLElement>) {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const p = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.4 });
  const scale = useTransform(p, [0, 1], [1, 1.18]);
  const y = useTransform(p, [0, 1], ["0%", "16%"]);
  const ctaY = useTransform(p, [0, 1], [0, -140]);
  const ctaOpacity = useTransform(p, [0, 0.55], [1, 0]);
  const shade = useTransform(p, [0, 1], [0, 0.6]);
  return {
    media: reduce ? undefined : { scale, y },
    cta: reduce ? undefined : { y: ctaY, opacity: ctaOpacity },
    shade,
  };
}

const useAccraClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // Accra is UTC+0 year-round.
  return now.toISOString().slice(11, 19);
};

export function HeroChrome({ label, shade }: { label: string; shade: MotionValue<number> }) {
  const clock = useAccraClock();
  return (
    <>
      <motion.div aria-hidden className="pointer-events-none absolute inset-0 z-[5] bg-[#0A1220]" style={{ opacity: shade }} />
      <div aria-hidden className="pointer-events-none absolute inset-0 z-[6] overflow-hidden">
        <div className="fx-scanline absolute inset-x-0 h-1/3" />
      </div>
      <div aria-hidden className="pointer-events-none absolute inset-4 md:inset-8 z-[7] hidden sm:block">
        <span className="fx-hud-corner left-0 top-0 border-l border-t" />
        <span className="fx-hud-corner right-0 top-0 border-r border-t" />
        <span className="fx-hud-corner left-0 bottom-16 border-l border-b" />
        <span className="fx-hud-corner right-0 bottom-16 border-r border-b" />
        <div className="absolute left-8 top-1 font-mono text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/75">
          {label}
        </div>
        <div className="absolute right-8 top-1 font-mono text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/75 tabular-nums">
          <span className="fx-blink mr-2 inline-block h-1.5 w-1.5 translate-y-[-1px] rounded-full bg-[hsl(var(--hibane-sky))]" />
          Accra {clock} GMT
        </div>
        <div className="absolute left-8 bottom-[4.6rem] font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
          5.6037° N, 0.1870° W
        </div>
      </div>
    </>
  );
}

/** Wrapper that drifts toward the cursor; the label inside moves further for depth. */
export function Magnetic({
  children,
  strength = 14,
  className = "inline-block",
}: {
  children: ReactNode;
  strength?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const set = (x: number, y: number) => {
    if (ref.current) ref.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    if (inner.current) inner.current.style.transform = `translate3d(${x * 0.5}px, ${y * 0.5}px, 0)`;
  };
  return (
    <div
      ref={ref}
      className={`${className} transition-transform duration-300 ease-out`}
      onPointerMove={(e) => {
        if (reduce || e.pointerType !== "mouse") return;
        const r = e.currentTarget.getBoundingClientRect();
        set(((e.clientX - r.left) / r.width - 0.5) * strength * 2, ((e.clientY - r.top) / r.height - 0.5) * strength * 2);
      }}
      onPointerLeave={() => set(0, 0)}
    >
      <div ref={inner} className="transition-transform duration-300 ease-out">
        {children}
      </div>
    </div>
  );
}
